import { useState } from 'react';
import { enqueueSnackbar } from 'notistack';

import Card from '@mui/material/Card';
import Grid from '@mui/system/Unstable_Grid';
import { Stack, Button } from '@mui/material';

import { useBoolean } from 'src/hooks/use-boolean';

import { useTranslate } from 'src/locales';
import { useGetCandidateDocumentsQuery, useRemoveCandidateDocumentMutation } from 'src/services/candidates/candidates.service';

import { useTable } from 'src/components/table';
import FileManagerTable from 'src/components/documents/file-manager-table';

import CandidateDocumentUploadModal from '../../modal/candidate-document-uploader';

const DEFAULT_PAGE = {
  page: 1,
  per_page: 10,
};

type Props = {
  candidateId?: string;
  uploadVisible?: boolean;
};

export default function CandidateDocumentsView({ candidateId, uploadVisible = true }: Props) {
  const [page, setPage] = useState(DEFAULT_PAGE);

  const showUploadModal = useBoolean();

  const { t } = useTranslate();

  const [removeDocument, { isLoading: isRemoving }] = useRemoveCandidateDocumentMutation();

  const { currentData: candidateDocuments, isFetching } = useGetCandidateDocumentsQuery(
    {
      candidate_id: candidateId as string,
      params: page,
    },
    { skip: !candidateId }
  );

  const table = useTable();

  const notFound = !candidateDocuments?.count && !isFetching;

  const onAddSuccess = () => {
    showUploadModal.onFalse();
  };

  const handleDeleteItem = async (id: string) => {
    try {
      await removeDocument({
        candidate_doc_id: id,
        candidate_id: candidateId as string
      }).unwrap();

      enqueueSnackbar(t('candidates.api.delete_document.success'), { variant: 'success' });

    } catch (error) {
      enqueueSnackbar(t('candidates.api.delete_document.default_error'), { variant: 'error' });
    }
  };

  return (
    <Grid container spacing={3}>
      {
        uploadVisible && (
          <Grid xs={12}>
            <Stack direction="row" justifyContent="flex-end" mt={2}>
              <Button variant="contained" disabled={!candidateId} onClick={showUploadModal.onTrue}>
                {t('profile.documents.upload')}
              </Button>
            </Stack>
          </Grid>
        )
      }
      <Grid xs={12}>
        <Card>
          <FileManagerTable
            table={table}
            isLoading={isFetching}
            dataFiltered={candidateDocuments?.results ?? []}
            count={candidateDocuments?.count ?? 0}
            onDeleteRow={handleDeleteItem}
            notFound={notFound}
            documentType="company_profile"
            onChangePage={(_e, pageNumber) => setPage({ ...page, page: pageNumber + 1 })}
            onChangePerPage={(e) => setPage({ ...page, per_page: Number(e.target.value) })}
            pageOptions={page}
          />
        </Card>
      </Grid>

      {
        candidateId && (
          <CandidateDocumentUploadModal
            open={showUploadModal.value}
            onClose={showUploadModal.onFalse}
            candidateId={candidateId}
            onUpload={onAddSuccess}
          />
        )
      }
    </Grid>
  );
}